import React, { useState, useEffect, useRef } from "react";
import BackgroundImage from "../common/BackgroundImage";
import { BACKGROUNDS } from "../../config/backgrounds";
import { getAllTexts, createArticle, saveCorkboardLayout, updateText, getDailyFocus, saveDailyFocus, getChildren, folderProgress } from "../../services/workspaceService";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useEditLock } from "../../context/EditLockContext";
import { CheckSquare, Plus, ChevronRight, ChevronLeft, Edit3, BookOpen, Eye, PenLine, Loader2, Map as MapIcon, HelpCircle, Layers } from "lucide-react";
import Usable from "../common/Usable";
import { VERSION } from "../../config/version";
import deskGlass from "../../styles/deskGlass";
import TextEditor from "../workspace/TextEditor";
import DeskSwiper from "../common/DeskSwiper";
import { Responsive, WidthProvider } from "react-grid-layout";
import ProjectFolders from "./ProjectFolders";
import { navigateToText } from "../../utils/textRoutes";
import { rememberWritingText } from "../../utils/writingSession";

const styles = deskGlass;

const ResponsiveGridLayout = WidthProvider(Responsive);

const SLIDE_LABELS = ["Guía", "Enfoque", "Proyectos", "Relatos", "Tablero"];
const NARROW_MQ = "(max-width: 640px)";

const DASH_MOBILE_CSS = `
  @media (max-width: 640px) {
    .dashboard-greeting { font-size: 1.25rem !important; }
    .dash-desk-content { padding: 12px 14px 80px !important; max-width: 100% !important; }
    .dash-tablero-stack { display: flex; flex-direction: column; gap: 12px; }
  }
`;

function QuickCapture({ quickThought, setQuickThought, isSaving, showSuccess, successMsg, onSave, onKeyDown, disabled }) {
  return (
    <section style={styles.glassSection}>
      <div style={styles.glassHeader}>
        <PenLine size={20} color="#fff" />
        <h2 style={styles.glassTitle}>Captura Veloz</h2>
      </div>
      <div style={{ position: 'relative', display: 'flex', gap: '10px' }}>
        <textarea 
          id="quick-capture-textarea"
          autoComplete="off"
          placeholder="Escribí una idea rápida..." 
          value={quickThought}
          onChange={(e) => setQuickThought(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          style={{ ...styles.glassTextarea, opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "text" }}
        />
        <button 
          onClick={onSave} 
          disabled={disabled || isSaving || !quickThought.trim()} 
          style={{ 
            ...styles.glassActionBtn,
            width: '44px', height: '44px',
            backgroundColor: showSuccess ? "rgba(34, 197, 94, 0.2)" : "rgba(255,255,255,0.05)",
            borderColor: showSuccess ? "#4ade80" : "rgba(255,255,255,0.1)",
            color: showSuccess ? "#4ade80" : "#fff",
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {isSaving ? <Loader2 size={18} className="animate-spin" /> : showSuccess ? "✓" : <Plus size={20} />}
        </button>
      </div>
    </section>
  );
}

function AttentionHeader({ pendingFocus, readingListCount, firstTask }) {
  const getMessage = () => {
    if (pendingFocus > 0) {
      return pendingFocus === 1
        ? "Tenés 1 prioridad pendiente en tu enfoque de hoy."
        : `Tenés ${pendingFocus} prioridades pendientes en tu enfoque de hoy.`;
    }
    if (firstTask) {
      return `Próxima tarea: ${firstTask}`;
    }
    if (readingListCount > 0) {
      return readingListCount === 1
        ? "Tenés 1 lectura pendiente en tu lista."
        : `Tenés ${readingListCount} lecturas pendientes en tu lista.`;
    }
    return "Todo al día. Nada reclama tu atención por ahora.";
  };

  return (
    <div>
      <h1 className="dashboard-greeting" style={styles.greeting}>
        <Eye size={28} className="greeting-icon" style={{ verticalAlign: 'middle', marginRight: '12px', opacity: 0.8 }} />
        {getMessage()}
      </h1>
      <p style={styles.subtext}>
        <span style={{ color: '#D4AF37', fontSize: '1rem', fontStyle: 'normal', opacity: 0.9, fontWeight: 'bold' }}>VERSION {VERSION}</span>
      </p>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { settings } = useWorkspace();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [recentTexts, setRecentTexts] = useState([]);
  const [pinnedTexts, setPinnedTexts] = useState([]);
  const [readingList, setReadingList] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickThought, setQuickThought] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("¡Registrado en el Vault!");
  const [dailyFocus, setDailyFocus] = useState([]);
  const [allTexts, setAllTexts] = useState([]);
  const [projectFolders, setProjectFolders] = useState([]);
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.matchMedia(NARROW_MQ).matches : false
  );

  // Swiper state
  const [activeSlide, setActiveSlide] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [undoAction, setUndoAction] = useState(null);
  const deskSwiperRef = useRef(null);

  // Editor state
  const [editor, setEditor] = useState({ isOpen: false, text: null, mode: "edit" });

  const loadDailyFocus = async () => {
    try {
      const focus = await getDailyFocus();
      setDailyFocus(focus.length ? focus : [{ text: "", done: false }, { text: "", done: false }, { text: "", done: false }]);
    } catch (e) {
      console.error("Daily Focus Load Error:", e);
    }
  };

  const loadData = async () => {
    try {
      const all = await getAllTexts();
      const sorted = [...all].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      const topLevel = sorted.filter((t) => !t.parentId);

      setAllTexts(all);
      setPinnedTexts(topLevel.filter((t) => t.isPinned));
      setReadingList(topLevel.filter((t) => !t.isRead && !t.isPinned).slice(0, 5));
      setRecentTexts(topLevel.filter((t) => t.isRead && !t.isPinned).slice(0, 8));
      setProjectFolders(
        all
          .filter((t) => t.kind === "folder" && !t.parentId)
          .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
      );

      const tasks = all
        .map((t) => {
          if (t.kind === "folder") {
            const kids = getChildren(all, t.id);
            const { done, total } = folderProgress(kids);
            return { ...t, pendingCount: Math.max(0, total - done) };
          }
          const lines = (t.content || "").split("\n");
          const pendingEntries = lines.filter((l) => l.includes("[ ]"));
          return { ...t, pendingCount: pendingEntries.length };
        })
        .filter((t) => t.pendingCount > 0 && !t.parentId);
      setPendingTasks(tasks.slice(0, 5));
    } catch (e) {
      console.error("Dashboard Load Error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDailyFocus();
    loadData();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(NARROW_MQ);
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const handleSaveThought = async () => {
    if (locked) return;
    if (!quickThought.trim()) return;
    setIsSaving(true);
    try {
      const content = quickThought.trim();
      const isPriority = content.includes("!");
      if (isPriority) {
        const cleanTask = content.replace(/!|#\w+|\[\s*\]/g, "").trim();
        if (cleanTask) {
          const next = [...dailyFocus];
          const emptyIndex = next.findIndex(f => !f.text.trim());
          if (emptyIndex !== -1) next[emptyIndex] = { text: cleanTask, done: false };
          else next.push({ text: cleanTask, done: false });
          setDailyFocus(next);
          await saveDailyFocus(next);
        }
      }
      const autoTitle = content.split('\n')[0].slice(0, 30).trim() + (content.length > 30 ? "..." : "");
      const saved = await createArticle(autoTitle, content, null, {});
      setQuickThought("");
      setSuccessMsg(`Guardado en: ${saved.deskName} > ${saved.drawerName} > ${saved.notebookName}`);
      setShowSuccess(true);
      await loadData();
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (e) {
      console.error("Save Error:", e);
      setSuccessMsg("Error al guardar");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (locked) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSaveThought();
    }
  };

  const handleFocusChange = (index, value) => {
    const next = [...dailyFocus];
    next[index] = { ...next[index], text: value };
    setDailyFocus(next);
  };

  const handleFocusToggle = async (index) => {
    if (locked) return;
    const prev = [...dailyFocus];
    const next = [...dailyFocus];
    next[index] = { ...next[index], done: !next[index].done };
    setDailyFocus(next);
    await saveDailyFocus(next);
    if (next[index].done) {
      setUndoAction({ type: "focus", data: prev });
      setTimeout(() => setUndoAction(null), 5000);
    }
  };

  const handleFocusBlur = async () => {
    if (locked) return;
    const cleaned = dailyFocus.map(f => ({ text: (f.text || "").trim(), done: !!f.done }));
    setDailyFocus(cleaned);
    await saveDailyFocus(cleaned);
  };

  const handleUndo = async () => {
    if (locked) return;
    if (!undoAction) return;
    if (undoAction.type === "focus") {
      setDailyFocus(undoAction.data);
      await saveDailyFocus(undoAction.data);
    }
    setUndoAction(null);
  };

  const openReader = (text) => {
    if (!text) return;
    rememberWritingText(text);
    navigateToText(navigate, text);
  };
  const openEditor = (mode, text = null) => {
    if (locked) return;
    setEditor({ isOpen: true, mode, text });
  };
  const closeEditor = () => setEditor({ isOpen: false, text: null });

  const handleSaveText = async (title, content, img, tags = []) => {
    if (locked) return;
    if (editor.mode === "edit" && editor.text) {
      await updateText(editor.text.deskId, editor.text.drawerId, editor.text.notebookId, editor.text.id, { title, content, img, tags });
    } else {
      await createArticle(title, content, img, { tags });
    }
    closeEditor();
    await loadData();
  };

  const handleLayoutChange = (newLayout) => {
    if (locked) return;
    saveCorkboardLayout(newLayout);
  };

  const buildLayout = () => {
    const cloudLayout = settings.corkboardLayout || [];
    const savedMap = new Map(cloudLayout.map(item => [item.i, item]));
    const layout = [];
    const all = [...pinnedTexts, ...readingList];

    all.forEach((t) => {
      if (savedMap.has(t.id)) {
        layout.push({ ...savedMap.get(t.id), i: t.id });
      } else {
        const w = 4, h = 4, cols = 12;
        let x = 0, y = 0;
        let placed = false;
        function checkOverlap(ax, ay, aw, ah) {
          return layout.some(l => l.x < ax + aw && l.x + l.w > ax && l.y < ay + ah && l.y + l.h > ay);
        }
        while (!placed) {
          if (!checkOverlap(x, y, w, h)) {
            layout.push({ i: t.id, x, y, w, h });
            placed = true;
          } else {
            x += w;
            if (x + w > cols) { x = 0; y += h; }
          }
        }
      }
    });
    return layout;
  };

  if (loading) return (
    <div style={styles.center}>
      <BackgroundImage image={BACKGROUNDS.dashboard} />
      <div style={{ position: 'relative', zIndex: 10 }}>Sincronizando escritorio...</div>
    </div>
  );

  const layout = buildLayout();
  const slideCount = SLIDE_LABELS.length;

  const scrollToSlide = (i) => deskSwiperRef.current?.scrollToSlide(i);

  const deskSlides = [
    {
      label: "Guía",
      content: (
        <div style={styles.deskContent}>
          <div style={{ ...styles.glassSection, border: "2px solid rgba(255,255,255,0.15)" }}>
            <div style={styles.glassHeader}>
              <BookOpen size={20} color="#fff" />
              <h2 style={styles.glassTitle}>Guía del Vault</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", color: "rgba(255,255,255,0.8)", fontSize: "0.95rem", lineHeight: "1.5" }}>
              <p>✨ <b>Tus textos están seguros.</b> Al tocar un relato, se abre para lectura; nada se borra sin confirmar.</p>
              <p>↔️ <b>Deslizá horizontalmente</b> o usá <b>← →</b> y <b>Shift + rueda del mouse</b> para moverte entre Enfoque, Proyectos y Relatos.</p>
              <p>🎯 <b>Enfoque:</b> Tu lista de tareas y captura rápida por voz o texto.</p>
              <p>🗄️ <b>Cajones:</b> Usá el botón de la barra inferior para ver la estructura jerárquica completa.</p>
            </div>
            <div style={{ marginTop: "20px", display: "flex", gap: "12px", justifyContent: "center" }}>
              <button onClick={() => navigate("/help")} style={styles.viewToggle} title="Guía de uso">
                <HelpCircle size={18} />
                <span style={{ fontSize: "0.7rem", fontWeight: "bold", letterSpacing: "1px" }}>AYUDA COMPLETA</span>
              </button>
              <button onClick={() => navigate("/atlas")} style={styles.viewToggle} title="Ver Atlas del Vault">
                <MapIcon size={18} />
                <span style={{ fontSize: "0.7rem", fontWeight: "bold", letterSpacing: "1px" }}>ATLAS</span>
              </button>
            </div>
            <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "12px", alignItems: "center" }}>
              <button type="button" onClick={() => scrollToSlide(activeSlide - 1)} disabled={activeSlide === 0} style={styles.slideNavBtn}>
                <ChevronLeft size={18} /> Anterior
              </button>
              <span style={{ opacity: 0.5, fontSize: "0.8rem" }}>
                {SLIDE_LABELS[activeSlide]} — flechas, Shift+rueda, o puntos abajo
              </span>
              <button type="button" onClick={() => scrollToSlide(activeSlide + 1)} disabled={activeSlide === slideCount - 1} style={styles.slideNavBtn}>
                Siguiente <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "Enfoque",
      content: (
        <div style={styles.deskContent}>
          <AttentionHeader
            pendingFocus={dailyFocus.filter((f) => f.text.trim() && !f.done).length}
            readingListCount={readingList.length}
            firstTask={pendingTasks[0]?.topTasks?.[0]?.text}
          />
          <section style={styles.glassSection}>
            <div style={styles.glassHeader}>
              <Eye size={20} color="#fff" />
              <h2 style={styles.glassTitle}>Enfoque Diario</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {dailyFocus.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "4px 0" }}>
                  <button
                    onClick={() => handleFocusToggle(i)}
                    disabled={locked}
                    style={{
                      width: "22px", height: "22px", borderRadius: "50%",
                      border: item.done ? "none" : "2px solid rgba(255,255,255,0.3)",
                      background: item.done ? "#4ade80" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: locked ? "not-allowed" : "pointer",
                      flexShrink: 0,
                      opacity: locked ? 0.45 : 1,
                    }}
                  >
                    {item.done && <span style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>✓</span>}
                  </button>
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => handleFocusChange(i, e.target.value)}
                    onBlur={handleFocusBlur}
                    disabled={locked}
                    placeholder="Añadir prioridad..."
                    style={{
                      flex: 1, background: "transparent", border: "none",
                      color: item.done ? "rgba(255,255,255,0.3)" : "#fff",
                      fontSize: "1.05rem", outline: "none",
                      textDecoration: item.done ? "line-through" : "none",
                      opacity: locked ? 0.55 : 1,
                    }}
                  />
                </div>
              ))}
            </div>
          </section>
          <QuickCapture
            quickThought={quickThought}
            setQuickThought={setQuickThought}
            isSaving={isSaving}
            showSuccess={showSuccess}
            successMsg={successMsg}
            onSave={handleSaveThought}
            onKeyDown={handleKeyDown}
            disabled={locked}
          />
          <section style={styles.glassSection}>
            <div style={styles.glassHeader}>
              <CheckSquare size={20} color="#fff" />
              <h2 style={styles.glassTitle}>Tareas Activas</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {pendingTasks.slice(0, 3).map((t) => (
                <Usable
                  key={t.id}
                  location={[t.deskName, t.drawerName, t.notebookName, t.title]}
                  title={t.title}
                  onRename={locked ? undefined : (name) => updateText(t.deskId, t.drawerId, t.notebookId, t.id, { title: name }).then(loadData)}
                  copyLabel="Copiar enlace"
                  copyValue={`${window.location.origin}/notas/${t.dateSlug}/${t.slug}`}
                  onClick={() => navigate(`/notas/${t.dateSlug}/${t.slug}`)}
                  style={styles.glassListItem}
                >
                  <span style={{ fontSize: "0.9rem", color: "#fff", fontWeight: "600" }}>{t.title}</span>
                  <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", marginLeft: "auto" }}>{t.pendingCount} pnd</span>
                </Usable>
              ))}
            </div>
          </section>
        </div>
      ),
    },
    {
      label: "Proyectos",
      content: (
        <div style={styles.deskContent} className="dash-desk-content">
          <h1 className="dashboard-greeting" style={styles.greeting}>Proyectos</h1>
          <p style={{ ...styles.subtext, marginBottom: 16 }}>
            Nota-carpeta: cada ítem del checklist es una nota (puede ser otra carpeta).
          </p>
          <ProjectFolders
            projects={projectFolders}
            allTexts={allTexts}
            locked={locked}
            onRefresh={loadData}
            onOpenText={openReader}
          />
        </div>
      ),
    },
    {
      label: "Relatos",
      content: (
        <div style={styles.deskContent} className="dash-desk-content">
          <h1 className="dashboard-greeting" style={styles.greeting}>Relatos</h1>
          <section style={styles.glassSection}>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {recentTexts.map((t) => (
                <Usable
                  key={t.id}
                  location={[t.deskName, t.drawerName, t.notebookName, t.title]}
                  title={t.title}
                  onRename={locked ? undefined : (name) => updateText(t.deskId, t.drawerId, t.notebookId, t.id, { title: name }).then(loadData)}
                  copyLabel="Copiar enlace"
                  copyValue={`${window.location.origin}/notas/${t.dateSlug}/${t.slug}`}
                  onClick={() => navigate(`/notas/${t.dateSlug}/${t.slug}`)}
                  style={styles.glassListItem}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>{t.notebookName}</div>
                    <div style={{ fontSize: "1rem", color: "#fff", fontWeight: "600" }}>{t.title}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "0.7rem", opacity: 0.3, fontWeight: "bold" }}>LEER</span>
                    <ChevronRight size={18} color="rgba(255,255,255,0.2)" />
                  </div>
                </Usable>
              ))}
            </div>
          </section>
        </div>
      ),
    },
    {
      label: "Tablero",
      content: (
        <div style={{ ...styles.deskContent, maxWidth: isNarrow ? "100%" : "1000px" }} className="dash-desk-content">
          <h1 className="dashboard-greeting" style={styles.greeting}>Tablero</h1>
          {isNarrow ? (
            <div className="dash-tablero-stack">
              {[...pinnedTexts, ...readingList].map((t) => (
                <section key={t.id} style={styles.glassSection}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, opacity: 0.5 }}>
                    <Layers size={14} />
                    {!locked && (
                      <Edit3 size={14} style={{ cursor: "pointer" }} onClick={() => openEditor("edit", t)} />
                    )}
                  </div>
                  <Usable
                    location={[t.deskName, t.drawerName, t.notebookName, t.title]}
                    title={t.title}
                    onRename={locked ? undefined : (name) => updateText(t.deskId, t.drawerId, t.notebookId, t.id, { title: name }).then(loadData)}
                    copyLabel="Copiar enlace"
                    copyValue={`${window.location.origin}/notas/${t.dateSlug}/${t.slug}`}
                    onClick={() => openReader(t)}
                    bodyStyle={{ display: "block", flex: 1 }}
                    style={{ cursor: "pointer" }}
                  >
                    <div style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#fff", marginBottom: 4 }}>{t.title}</div>
                    <div style={{ fontSize: "0.75rem", opacity: 0.6 }}>
                      {(t.content || "").replace(/<[^>]*>?/gm, "").slice(0, 80)}
                      {(t.content || "").length > 80 ? "…" : ""}
                    </div>
                  </Usable>
                </section>
              ))}
            </div>
          ) : (
            <ResponsiveGridLayout
              className="layout"
              layouts={{ lg: layout }}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={100}
              isDraggable={!locked}
              isResizable={!locked}
              draggableHandle=".glass-drag-handle"
              onLayoutChange={handleLayoutChange}
              onDragStart={() => setIsDragging(true)}
              onDragStop={() => setIsDragging(false)}
            >
              {[...pinnedTexts, ...readingList].map((t) => (
                <div key={t.id} style={styles.glassSection}>
                  <div
                    className="glass-drag-handle"
                    style={{
                      cursor: locked ? "not-allowed" : "move",
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                      opacity: locked ? 0.25 : 0.4,
                      filter: locked ? "grayscale(1)" : "none",
                    }}
                  >
                    <Layers size={14} />
                    <Edit3
                      size={14}
                      style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.45 : 1 }}
                      onClick={locked ? undefined : () => openEditor("edit", t)}
                    />
                  </div>
                  <Usable
                    location={[t.deskName, t.drawerName, t.notebookName, t.title]}
                    title={t.title}
                    onRename={locked ? undefined : (name) => updateText(t.deskId, t.drawerId, t.notebookId, t.id, { title: name }).then(loadData)}
                    copyLabel="Copiar enlace"
                    copyValue={`${window.location.origin}/notas/${t.dateSlug}/${t.slug}`}
                    onClick={() => navigate(`/notas/${t.dateSlug}/${t.slug}`)}
                    bodyStyle={{ display: "block", flex: 1 }}
                    style={{ cursor: "pointer", flex: 1, overflow: "hidden" }}
                  >
                    <div style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#fff", marginBottom: "4px" }}>{t.title}</div>
                    <div style={{ fontSize: "0.75rem", opacity: 0.6 }}>{t.content?.replace(/<[^>]*>?/gm, "").slice(0, 50)}...</div>
                  </Usable>
                </div>
              ))}
            </ResponsiveGridLayout>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={styles.container}>
      <style>{DASH_MOBILE_CSS}</style>
      <BackgroundImage image={BACKGROUNDS.dashboard} />

      {undoAction && (
        <div style={styles.undoToast}>
          <span style={{ fontSize: "0.9rem" }}>Tarea movida/completada</span>
          <button onClick={handleUndo} style={styles.undoBtn}>DESHACER</button>
        </div>
      )}

      <DeskSwiper
        ref={deskSwiperRef}
        slides={deskSlides}
        initial={1}
        onSlideChange={setActiveSlide}
        scrollDisabled={isDragging}
      />

      {/* Editor Overlay */}
      {editor.isOpen && (
        <TextEditor
          isOpen={editor.isOpen}
          onClose={closeEditor}
          onSave={handleSaveText}
          initialTitle={editor.text?.title || ""}
          initialContent={editor.text?.content || ""}
          initialImg={editor.text?.img || ""}
          mode={editor.mode}
        />
      )}
    </div>
  );
}

export default Dashboard;
